import { useState } from 'react';

import classnames from 'classnames';
import { USER_ROLES } from 'cpool-constants';
import { isLeadCarryingRole } from 'cpool-utils';

import LeadTrack from '../LeadTrack.tsx';
import { ChatButton } from './menu/chat/ChatButton.tsx';
import Hamburger from './menu/Hamburger.tsx';
import NavLogo from './menu/NavLogo.tsx';
import { NotificationsMenu } from './menu/notifications/NotificationsMenu.tsx';
import { Representation } from './menu/representation/Representation.tsx';
import { SearchProvider } from './menu/search/contexts/SearchContext.tsx';
import Search from './menu/search/index.tsx';
import RealLeadTrack from './menu/tracking/RealLeadTrack.tsx';
import ImpersonationRevoke from './menu/user/ImpersonationRevoke.tsx';
import UserSettings from './menu/user/settings/UserSettings.tsx';
import { BucketSummary } from './navigation/routes/BucketSummary.tsx';
import { Communication } from './navigation/routes/Communication.tsx';
import Dashboard from './navigation/routes/Dashboard.tsx';
import { Management } from './navigation/routes/Management.tsx';
import Summary from './navigation/routes/Summary.tsx';
import useUserInfo from '../../hooks/useUserInfo';
import { ReportBugHeaderButton } from '~/components/ReportBugButton';
import { VITE_MODE } from '~/config.ts';
import { NotificationsLiveUpdates } from '~/features/notifications/components/NotificationsLiveUpdates';
import { ProcessedNotificationsTracker } from '~/features/notifications/components/ProcessedNotificationsTracker';
import { useInboxSocket } from '~/features/whatsapp/hooks/useInboxSocket';

const Header = () => {
    const [hamburgerMenu, setHamburgerMenu] = useState(false);
    const { impersonator, ownedBuckets, role } = useUserInfo() ?? {
        impersonator: undefined,
        ownedBuckets: [],
        role: null,
    };

    useInboxSocket();

    const hasAdminRole = role === USER_ROLES.admin;
    const hasTeamLeaderRole = role === USER_ROLES.team_leader;
    const hasCorpLeaderRole = role === USER_ROLES.corporation_director;
    const hasDivisionLeaderRole = role === USER_ROLES.division_leader;
    const hasAdviserRole = role === USER_ROLES.adviser;
    // a team leader carries their own leads, so everything an adviser gets for their own work applies
    const carriesLeads = isLeadCarryingRole(role);
    const hasEmailManagerRole = role === USER_ROLES.email_manager;
    const hasAccountantManagerRole = role === USER_ROLES.accountant_manager;
    const hasOwnBuckets = ownedBuckets && ownedBuckets.length > 0;
    const isStageSuitableForSwitcher = VITE_MODE === 'dev' || VITE_MODE === 'staging';
    const isImpersonated = !!impersonator;
    // Matches the roles actually granted CHAT resolvers/commands in the ACL role files (adviser,
    // teamLeader, divisionLeader, admin) - corporationDirector/accountantManager/emailManager have
    // no chat grant at all.
    const hasChatAccess = hasAdviserRole || hasTeamLeaderRole || hasDivisionLeaderRole || hasAdminRole;

    const headerButtons = (
        <>
            {carriesLeads && <Representation />}
            {hasAdminRole && isStageSuitableForSwitcher && <RealLeadTrack />}
            <LeadTrack />
            {hasChatAccess && <ChatButton />}
            {carriesLeads && <NotificationsMenu />}
            {isImpersonated && <ImpersonationRevoke />}
            <ReportBugHeaderButton />
            <UserSettings />
        </>
    );

    const classNameDropdown = classnames('fixed top-12 inset-x-0 hd:static hd:!block', !hamburgerMenu && 'hidden');

    return (
        <div className="sticky top-0 z-50">
            {/* outside headerButtons — that fragment renders twice (desktop + mobile), these must mount once */}
            {carriesLeads && <ProcessedNotificationsTracker />}
            {carriesLeads && <NotificationsLiveUpdates />}
            <div className="bg-surface-light py-1 px-3 sm:px-4 xl:px-8">
                <div className="flex items-center gap-2">
                    <Hamburger menuState={hamburgerMenu} setMenuState={setHamburgerMenu} />
                    <NavLogo />
                    <SearchProvider>
                        <Search />
                    </SearchProvider>
                    <div className="hidden hd:flex hd:items-center hd:gap-2">{headerButtons}</div>
                </div>
            </div>
            <div className={classNameDropdown}>
                <div className="bg-surface-light px-3 sm:px-4 py-2 hd:hidden">
                    <div className="flex flex-wrap gap-2">{headerButtons}</div>
                </div>
                <div className="bg-blue-dark py-1.5 hd:px-4 xl:px-8">
                    <div className="flex flex-col gap-4 hd:flex hd:flex-row hd:items-center">
                        {carriesLeads && <Dashboard />}
                        {(hasAdviserRole ||
                            hasTeamLeaderRole ||
                            hasCorpLeaderRole ||
                            hasDivisionLeaderRole ||
                            hasAccountantManagerRole) && <Summary />}
                        {hasOwnBuckets && <BucketSummary />}
                        {hasChatAccess && <Communication />}
                        {(hasAdminRole ||
                            hasTeamLeaderRole ||
                            hasCorpLeaderRole ||
                            hasDivisionLeaderRole ||
                            hasEmailManagerRole ||
                            hasAccountantManagerRole) && <Management />}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Header;
